import { Injectable } from '@angular/core';
import {
  ButtonColor,
  CustomerKbaStatus,
  CommonActionKey,
  HorizontalListData,
  HorizontalListFactory,
  HorizontalListOptionType,
  SimpleDialogData,
} from 'common';
import {
  IDVerseAttempt,
  IDVerseOverallResult,
  IDVerseOverallResultLabel,
  IDVerseRiskLevel,
  IDVerseRiskLevelLabel,
  KBAData,
  KBAStatusMap,
} from '../../customer.model';
import { ActivityItem } from './customer-identity-verification-activity-widget.model';

export const IDVerseRiskLevelLabels = {
  [IDVerseRiskLevel.LowRisk]: {
    color: 'mat-green-bg',
    icon: 'check_circle',
    label: IDVerseRiskLevelLabel.get(IDVerseRiskLevel.LowRisk),
  },
  [IDVerseRiskLevel.HighRisk]: {
    color: 'mat-orange-bg',
    icon: 'error',
    label: IDVerseRiskLevelLabel.get(IDVerseRiskLevel.HighRisk),
  },
};

export const IDVerseOverallResultLabels = {
  [IDVerseOverallResult.Fail]: {
    color: 'mat-red-bg',
    icon: 'error',
    label: IDVerseOverallResultLabel.get(IDVerseOverallResult.Fail),
  },
  [IDVerseOverallResult.Pass]: {
    color: 'mat-green-bg',
    icon: 'check_circle',
    label: IDVerseOverallResultLabel.get(IDVerseOverallResult.Pass),
  },
};

export const KBAStatusLabels = {
  [CustomerKbaStatus.None]: { color: 'mat-grey-bg', icon: 'help', label: KBAStatusMap.get(CustomerKbaStatus.None) },
  [CustomerKbaStatus.Success]: { color: 'mat-green-bg', icon: 'check_circle', label: KBAStatusMap.get(CustomerKbaStatus.Success) },
  [CustomerKbaStatus.Failed]: { color: 'mat-red-bg', icon: 'error', label: KBAStatusMap.get(CustomerKbaStatus.Failed) },
};

@Injectable()
export class CustomerIdentityVerificationActivityWidgetAdapter {
  adaptActivityListData = (idVerseAttempts: IDVerseAttempt[], kbaData: KBAData, canInvalidate: boolean): ActivityItem[] => {
    const idVerseActivityItems = this.adaptIdVerseActivityItemsData(idVerseAttempts, canInvalidate) || [];
    const kbaActivityItem = this.adaptKBAActivityItemData(kbaData);

    const allActivityItems = [...idVerseActivityItems];
    if (kbaActivityItem) {
      allActivityItems.push(kbaActivityItem);
    }

    return allActivityItems.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateA - dateB;
    });
  };

  private adaptIdVerseActivityItemsData = (idVerseAttempts: IDVerseAttempt[], canInvalidate: boolean): ActivityItem[] => {
    return idVerseAttempts?.map((attempt) => {
      return {
        title: 'IDVerse attempt',
        date: new Date(attempt?.createdOnUtc),
        idVerseAttempt: attempt,
        listItems: this.adaptIdVerseAttemptListData(attempt),
        showActionsMenu: true,
        showInvalidateAction: (attempt?.status === 'PENDING' || attempt?.status === 'INPROGRESS') && canInvalidate,
      }
    })
  }

  private adaptIdVerseAttemptListData = (data: IDVerseAttempt): HorizontalListData[] => {
    return [
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Reference',
        displayedValue: data?.reference,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'IDVerse status',
        displayedValue: data?.status,
      }),
      HorizontalListFactory.createKeyValueCustomValueTemplateItem({
        title: 'Document fraud analysis',
        customTemplateName: 'riskLevelValue',
      }),
      HorizontalListFactory.createKeyValueCustomValueTemplateItem({
        title: 'Data matching',
        customTemplateName: 'dataMatchingValue',
      }),
      HorizontalListFactory.createKeyValueCustomValueTemplateItem({
        title: 'Liveness',
        customTemplateName: 'livenessValue',
      }),
      HorizontalListFactory.createKeyValueCustomValueTemplateItem({
        title: 'Face match',
        customTemplateName: 'faceMatchValue',
      }),
      HorizontalListFactory.createKeyValueCustomItem({
        titleData: {
          optionType: HorizontalListOptionType.ExternalLink,
          displayedValue: 'IDVerse report',
          externalLink: data?.reportUrl,
        },
        valueData: {
          optionType: HorizontalListOptionType.CustomTemplate,
          customTemplateName: 'reportUrlRedirectAction',
        },
      }),
    ];
  };

  private adaptKBAActivityItemData = (data: KBAData): ActivityItem => {
    if (!data?.status || data.status === CustomerKbaStatus.None) {
      return null;
    }
    return {
      title: 'KBA attempt',
      kba: data,
      date: new Date(data?.lastVerificationDate),
      listItems: this.adaptKBAListData(data),
    };
  };

  private adaptKBAListData = (data: KBAData): HorizontalListData[] => {
    return [
      HorizontalListFactory.createKeyValueCustomValueTemplateItem({
        title: 'KBA Status',
        customTemplateName: 'kbaStatusValue',
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Last error',
        displayedValue: data?.lastError || '-',
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Attempts',
        displayedValue: data?.attemptCount?.toString(),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Last configuration',
        displayedValue: data?.lastConfigurationName || '-',
      }),
    ];
  };
}

export const getRefreshDialogData = (): SimpleDialogData => {
  return {
    title: 'Confirm refresh action?',
    body: 'Confirm below to refresh results of the attempt from the IDVerse server.',
    actions: [
      { label: 'CANCEL', buttonColor: ButtonColor.Default, actionKey: CommonActionKey.Cancel },
      { label: 'CONFIRM', buttonColor: ButtonColor.Primary, actionKey: CommonActionKey.Confirm },
    ],
  }
}

export const getInvalidateDialogData = (): SimpleDialogData => {
  return {
    title: 'Confirm invalidate action?',
    body: 'Confirm below to invalidate the IDVerse attempt.',
    actions: [
      { label: 'CANCEL', buttonColor: ButtonColor.Default, actionKey: CommonActionKey.Cancel },
      { label: 'CONFIRM', buttonColor: ButtonColor.Primary, actionKey: CommonActionKey.Confirm },
    ],
  };
};
